import React, { FormEvent, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useSubjectMenuList } from '@hooks/student.hook';
import { SubjectMenuList } from '@models/types/students.type';
import { convertToSlug } from '@utils/helpers/convertToSlug';
import CaretDown from '@assets/icons/caret-down';
import SubjectDropdown, {
  SubjectDropdownItem,
} from '@components/navigation/subject/SubjectDropdown';
import moment from 'moment';
import { useAtom, useAtomValue } from 'jotai';
import { activeSubjectSlug } from '@atom/activeSubjectStore';
import { LoadingScroll } from 'geniebook-ui/dist';
import { ISubjectSubscription } from '@models/interfaces/user.interface';
import { subjectSubscriptionsQueryAtom } from '@atom/globalQueryAtom';

export default function SubjectMenuListCard() {
  const querySubjectMenuList = useSubjectMenuList();
  const subjectSubscription = useAtomValue(subjectSubscriptionsQueryAtom);
  const [showDropdown, setShowDropdown] = useState(false);
  const [subjectList, setSubjectList] = useState<SubjectDropdownItem[]>([]);
  const { subjectId, menuId } = useParams();
  const dropdownPopupRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const [activeSubject, setActiveSubject] = useAtom(activeSubjectSlug);
  useEffect(() => {
    if (subjectId) {
      setActiveSubject(subjectId);
    }
  }, [subjectId]);

  useEffect(() => {
    const rawData = querySubjectMenuList.data?.data?.data;
    const subjectToPush: SubjectDropdownItem[] = [];
    if (rawData && Array.isArray(rawData)) {
      rawData.forEach((item, i: number) => {
        subjectToPush.push({
          key: item.subject + '_' + i.toString(),
          subjectId: parseInt(item.subject_id),
          label: item.display_name,
          url: `/${convertToSlug(item.display_name)}/worksheets`,
          expired: false,
          trial: false,
          order: i,
        });
      });
      setSubjectList(subjectToPush);
    }
  }, [querySubjectMenuList.data?.data]);

  useEffect(() => {
    if (subjectList.length > 0 && subjectSubscription.data) {
      processSubscriptionData(subjectSubscription.data.data?.data);
    }
  }, [subjectSubscription.data?.data, subjectList.length]);

  function processSubscriptionData(subscriptionData: ISubjectSubscription[]) {
    const subjects: SubjectDropdownItem[] = JSON.parse(
      JSON.stringify(subjectList),
    );

    if (Array.isArray(subscriptionData)) {
      for (let i = 0; i < subjects.length; i++) {
        const subjectItem = subjects[i];
        const item = subscriptionData.filter(
          value => value.subject_id == subjectItem.subjectId,
        )[0];
        if (!item?.expiry_date && !item?.is_trial_plan) {
          continue;
        }
        if (item.is_trial_plan) {
          subjectItem.trial = true;
        }
        if (item.expiry_date) {
          const now = moment();
          const expiryDate = moment
            .parseZone(item.expiry_date + '+08:00')
            .add(item.subscription_grace_period, 'day'); // from sgt time
          if (expiryDate.isBefore(now)) {
            subjectItem.expired = true;
          }
        }
        subjects.splice(i, 1, subjectItem);
      }
    }
    subjects.sort(function (a, b) {
      if (b.expired || a.expired) {
        return b.expired > a.expired ? -1 : 1;
      }
      return b.trial > a.trial ? -1 : 1;
    });
    setSubjectList(subjects);
  }

  // How to show or hide dropdown
  const handleDropdown = (e: FormEvent | MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setShowDropdown(!showDropdown);
  };

  function dropdownList(rawData: SubjectDropdownItem[]): SubjectDropdownItem[] {
    const list: SubjectDropdownItem[] = [];
    const target_location = ['worksheets', 'classes'].filter(
      loc => loc === menuId,
    ).length
      ? menuId
      : 'worksheets';

    // sort the raw data before showing the list, based on order, except its expired put them in the last
    rawData
      .sort((a, b) => {
        if (a.expired || b.expired) {
          return b.expired > a.expired ? -1 : 1;
        }
        return a.order - b.order;
      })
      .forEach(item => {
        list.push({
          ...item,
          url: `/${convertToSlug(item.label)}/${target_location}`,
        });
      });
    return list;
  }

  const handleClickOutside = (event: MouseEvent) => {
    if (
      dropdownPopupRef.current &&
      !dropdownPopupRef.current.contains(event.target as Node) &&
      dropdownRef.current &&
      !dropdownRef.current.contains(event.target as Node)
    ) {
      setShowDropdown(false);
    }
  };

  useEffect(() => {
    if (showDropdown) {
      document.addEventListener('click', handleClickOutside, true);
      return () => {
        document.removeEventListener('click', handleClickOutside, true);
      };
    }
  }, [showDropdown]);

  return (
    <div className="relative inline-block">
      <div
        className={'cursor-pointer'}
        onClick={e => handleDropdown(e)}
        ref={dropdownRef}
        data-testid={'dropdown-toggle'}
      >
        {querySubjectMenuList.isLoading && (
          <LoadingScroll isBlockOthers={true} />
        )}
        {!querySubjectMenuList.isLoading &&
          querySubjectMenuList.data?.data?.data
            .filter(el => convertToSlug(el.display_name) === activeSubject)
            .map((subject: SubjectMenuList, idx: number) => {
              return (
                <div key={idx}>
                  <span
                    id="subject-name"
                    className={
                      'font-black text-lg max-md:!text-md lg:text-primary-navyblue max-lg:text-neutral-whiteboard inline-block max-w-[250px] max-lg:max-w-full whitespace-nowrap text-ellipsis overflow-hidden'
                    }
                  >
                    {subject.display_name}
                  </span>
                  <CaretDown
                    className={
                      'ml-2 w-2.5 items-center inline align-super fill-current text-primary-apricot transition-all ' +
                      (showDropdown ? 'rotate-180' : '')
                    }
                    data-testid={'caret-down'}
                  />
                </div>
              );
            })}
      </div>
      {showDropdown && querySubjectMenuList.data?.data?.data ? (
        <div
          className={'absolute top-10 lg:left-0 max-lg:-left-10 max-md:-left-6'}
          ref={dropdownPopupRef}
        >
          <SubjectDropdown
            list={dropdownList(subjectList)}
            onClickItem={() => {
              setShowDropdown(false);
            }}
            closeDropdown={() => {
              setShowDropdown(false);
            }}
          />
        </div>
      ) : null}
    </div>
  );
}
